const LOCAL_API_URL = 'http://127.0.0.1:8000';
const RAILWAY_FRONTEND_HOST = 'deroyalty-frontend-production.up.railway.app';
const RAILWAY_API_URL = 'https://deroyalty-car-rental-backend-production-5646.up.railway.app';
const CUSTOM_API_URL = 'https://api.deroyaltycarrental.com';
const PUBLIC_API_URL = RAILWAY_API_URL;
const PRODUCTION_FRONTEND_HOSTS = [
    RAILWAY_FRONTEND_HOST,
    'deroyaltycarrental.com',
    'www.deroyaltycarrental.com',
];

function normalizeConfiguredApiUrl(configuredUrl) {
    const trimmedUrl = configuredUrl?.trim();
    if (!trimmedUrl) return '';

    if (trimmedUrl.replace(/\/$/, '') === CUSTOM_API_URL) {
        return RAILWAY_API_URL;
    }

    return trimmedUrl;
}

export function resolveApiBaseUrl(
    hostname = window.location.hostname,
    configuredUrl = process.env.REACT_APP_API_URL
) {
    const normalizedConfiguredUrl = normalizeConfiguredApiUrl(configuredUrl);
    if (normalizedConfiguredUrl) {
        return normalizedConfiguredUrl;
    }

    if (PRODUCTION_FRONTEND_HOSTS.includes(hostname)) {
        return PUBLIC_API_URL;
    }

    return LOCAL_API_URL;
}

// API Base URL - configure based on environment
export const API_BASE_URL = resolveApiBaseUrl();

const REQUEST_TIMEOUT_MS = 15000;

function buildUrl(path) {
    return `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function normalizeHeaders(headers) {
    const normalized = {};
    if (!headers) return normalized;

    Object.entries(headers).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            normalized[key] = value;
        }
    });

    return normalized;
}

function headersToObject(headers) {
    const result = {};
    headers?.forEach?.((value, key) => {
        result[key] = value;
    });
    return result;
}

async function parseResponseBody(response) {
    const text = await response.text();
    if (!text) return null;

    try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'string') {
            const trimmed = parsed.trim();
            if (
                (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
                (trimmed.startsWith('[') && trimmed.endsWith(']'))
            ) {
                try {
                    return JSON.parse(trimmed);
                } catch (error) {
                    return parsed;
                }
            }
        }
        return parsed;
    } catch (error) {
        return text;
    }
}

// FIX-09: Token is now stored in an HttpOnly cookie set by the backend.
// We keep a lightweight in-memory copy for route guards and UI state,
// but no longer persist it in localStorage.
let _inMemoryToken = null;
let _inMemoryRole = null;

export function setAuthToken(token, role) {
    _inMemoryToken = token;
    _inMemoryRole = (role || 'user').toLowerCase();
}

export function clearAuthToken() {
    _inMemoryToken = null;
    _inMemoryRole = null;
}

export function getAuthToken() {
    return _inMemoryToken;
}

export function getUserRole() {
    return _inMemoryRole;
}

export function isAuthenticated() {
    return !!_inMemoryToken;
}

function redirectAfterUnauthorized() {
    clearAuthToken();
    window.location.href = '/';
}

async function request(method, path, data, config = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const headers = normalizeHeaders(config.headers);
    const token = getAuthToken();
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const skipAuth = Boolean(config.skipAuth);
    const suppressUnauthorizedRedirect = Boolean(config.suppressUnauthorizedRedirect);

    // FIX-09: Send token as Bearer header AND rely on HttpOnly cookie (credentials: 'include')
    if (token && !skipAuth) {
        headers.Authorization = `Bearer ${token}`;
    }

    if (data !== undefined && !isFormData && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    if (isFormData) {
        delete headers['Content-Type'];
    }

    const fetchConfig = {
        method,
        credentials: 'include',
        headers,
        signal: controller.signal,
    };

    if (data !== undefined) {
        fetchConfig.body = isFormData ? data : JSON.stringify(data);
    }

    try {
        const response = await fetch(buildUrl(path), fetchConfig);
        const responseData = await parseResponseBody(response);
        const responseHeaders = headersToObject(response.headers);
        const normalizedResponse = {
            data: responseData,
            status: response.status,
            statusText: response.statusText,
            headers: responseHeaders,
            config: fetchConfig,
        };

        if (!response.ok) {
            const error = new Error(responseData?.message || response.statusText || 'Request failed');
            error.response = normalizedResponse;

            if (response.status === 401 && !suppressUnauthorizedRedirect) {
                redirectAfterUnauthorized();
            }

            throw error;
        }

        return normalizedResponse;
    } catch (error) {
        if (error.name === 'AbortError') {
            const timeoutError = new Error('Request timed out');
            timeoutError.code = 'ECONNABORTED';
            throw timeoutError;
        }

        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
}

const apiClient = {
    get: (path, config) => request('GET', path, undefined, config),
    post: (path, data, config) => request('POST', path, data, config),
    delete: (path, config) => request('DELETE', path, undefined, config),
};

export default apiClient;

// API Endpoint Functions
export const authAPI = {
    signup: (data) => apiClient.post('/user/signup/', data, { suppressUnauthorizedRedirect: true }),
    signin: (data) => apiClient.post('/user/signin/', data, { suppressUnauthorizedRedirect: true }),
    googleSignin: (accessToken) => apiClient.post('/user/google-signin/', { accessToken }, { suppressUnauthorizedRedirect: true }),
    logout: () => apiClient.post('/user/logout/'),
    getCurrentUser: (config = {}) => apiClient.get('/user/me/', config),
    changePassword: (data) => apiClient.post('/user/change-password/', data),
};

export const categoryAPI = {
    list: () => apiClient.get('/category/list/', { skipAuth: true }),
    create: (data) => apiClient.post('/category/create/', data),
    update: (id, data) => apiClient.post(`/category/update/${id}/`, data),
    delete: (id) => apiClient.delete(`/category/delete/${id}/`),
    getProducts: (id) => apiClient.get(`/category/show/${id}/`, { skipAuth: true }),
};

export const productAPI = {
    list: () => apiClient.get('/product/list/', { skipAuth: true }),
    create: (data) => apiClient.post('/product/add/', data),
    update: (id, data) => apiClient.post(`/product/update/${id}/`, data),
    delete: (id) => apiClient.delete(`/product/delete/${id}/`),
    find: (name) => apiClient.get(`/product/find/?name=${encodeURIComponent(name)}`, { skipAuth: true }),
};

export const cartAPI = {
    list: (config = {}) => apiClient.get('/cart/', config),
    add: (data) => apiClient.post('/cart/add/', data),
    delete: (id) => apiClient.delete(`/cart/delete/${id}/`),
};

export const wishlistAPI = {
    list: () => apiClient.get('/wishlist/'),
    add: (data) => apiClient.post('/wishlist/add/', data),
    delete: (id) => apiClient.delete(`/wishlist/delete/${id}/`),
};

export const contactAPI = {
    submit: (data) => apiClient.post('/contact/submit/', data),
    list: () => apiClient.get('/contact/list/'),
    find: (name) => apiClient.get(`/contact/find-message/?name=${encodeURIComponent(name)}`),
};

export const adminAPI = {
    getAccessPolicy: () => apiClient.get('/admin/access-policy/'),
    getUsers: () => apiClient.get('/admin/users/'),
    createUser: (data) => apiClient.post('/admin/users/', data),
    getUser: (id) => apiClient.get(`/admin/users/${id}/`),
    updateUser: (id, data) => apiClient.post(`/admin/users/${id}/`, data),
    deleteUser: (id) => apiClient.delete(`/admin/users/${id}/`),
    getMessages: () => apiClient.get('/admin/messages/'),
    createMessage: (data) => apiClient.post('/admin/messages/', data),
    getMessage: (id) => apiClient.get(`/admin/messages/${id}/`),
    updateMessage: (id, data) => apiClient.post(`/admin/messages/${id}/`, data),
    deleteMessage: (id) => apiClient.delete(`/admin/messages/${id}/`),
    getBookings: () => apiClient.get('/admin/bookings/'),
    getBooking: (id) => apiClient.get(`/admin/bookings/${id}/`),
    createBooking: (data) => apiClient.post('/admin/bookings/', data),
    updateBooking: (id, data) => apiClient.post(`/admin/bookings/${id}/`, data),
    deleteBooking: (id) => apiClient.delete(`/admin/bookings/${id}/`),
    getAllCarts: () => apiClient.get('/admin/all-cart-items/'),
    getAllWishlists: () => apiClient.get('/admin/all-wishlists/'),
    uploadImage: (formData) => apiClient.post('/admin/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const healthAPI = {
    check: () => apiClient.get('/health/', { skipAuth: true }),
};
